import React, { useEffect, useMemo, useState } from "react";
import { authApi } from "../api";
import {
  AlertTriangle,
  BarChart3,
  Boxes,
  IndianRupee,
  Package,
  Recycle,
  Scale,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  LineChart,
  Line,
} from "recharts";

const numberFmt = (n, d = 2) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

const Card = ({ title, value, icon: Icon, danger = false, suffix = "" }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-500">{title}</p>
        <h3 className={`mt-2 text-2xl font-bold ${danger ? "text-red-600" : "text-slate-800"}`}>
          {value}
          {suffix}
        </h3>
      </div>
      <div className={`p-2 rounded-lg ${danger ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600"}`}>
        <Icon size={18} />
      </div>
    </div>
  </div>
);

const SkeletonGrid = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="h-28 bg-slate-200 animate-pulse rounded-xl" />
    ))}
  </div>
);

const InventoryDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [kpis, setKpis] = useState(null);
  const [categoryUsage, setCategoryUsage] = useState([]);
  const [topWastage, setTopWastage] = useState([]);
  const [scrapHistory, setScrapHistory] = useState([]);

  const loadDashboard = async () => {
    setLoading(true);
    setError("");
    try {
      const [kpiRes, usageRes, wastageRes, scrapRes] = await Promise.all([
        authApi.get("/inventory/dashboard"),
        authApi.get("/inventory/category-usage", { params: { months: 6 } }),
        authApi.get("/inventory/top-wastage"),
        authApi.get("/inventory/scrap-pool"),
      ]);
      setKpis(kpiRes.data?.kpis || null);
      setCategoryUsage(usageRes.data?.data || []);
      setTopWastage(wastageRes.data?.data || []);
      setScrapHistory((scrapRes.data?.history || []).slice(0, 12).reverse());
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load inventory dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const wastagePie = useMemo(
    () => topWastage.map((r) => ({ name: r.name, value: Number(r.wastage || 0) })),
    [topWastage]
  );

  const pieColors = ["#3B82F6", "#EF4444", "#F59E0B", "#10B981", "#6366F1"];

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="p-4 md:p-6 space-y-4 md:space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-800">Inventory Dashboard</h1>
            <p className="text-sm text-slate-500">Live KPIs, usage analytics, wastage and dispatch history</p>
          </div>
        </div>

        {error && <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>}

        {loading ? (
          <SkeletonGrid />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <Card title="Total Stock Value" value={`Rs ${numberFmt(kpis?.total_stock_value)}`} icon={IndianRupee} />
              <Card title="Materials In Stock" value={numberFmt(kpis?.materials_in_stock, 0)} icon={Boxes} />
              <Card title="Low Stock Count" value={numberFmt(kpis?.low_stock_count, 0)} icon={AlertTriangle} danger={Number(kpis?.low_stock_count || 0) > 0} />
              <Card title="Issued This Month" value={numberFmt(kpis?.issued_this_month_grams, 3)} suffix=" g" icon={Package} />
              <Card title="Wastage This Month" value={numberFmt(kpis?.wastage_this_month_grams, 3)} suffix=" g" icon={Scale} danger={Number(kpis?.wastage_this_month_grams || 0) > 0} />
              <Card title="Scrap Pool Balance" value={numberFmt(kpis?.scrap_pool_grams, 3)} suffix=" g" icon={Recycle} />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <h2 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><BarChart3 size={16} /> Category Usage (Last 6 Months)</h2>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={categoryUsage}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="METAL_qty" fill="#3B82F6" />
                      <Bar dataKey="STONE_qty" fill="#10B981" />
                      <Bar dataKey="CONSUMABLE_qty" fill="#F59E0B" />
                      <Bar dataKey="FINDING_qty" fill="#EF4444" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <h2 className="font-semibold text-slate-800 mb-3">Top 5 Materials by Wastage</h2>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Tooltip />
                      <Pie data={wastagePie} dataKey="value" nameKey="name" outerRadius={110}>
                        {wastagePie.map((_, i) => (
                          <Cell key={i} fill={pieColors[i % pieColors.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <h2 className="font-semibold text-slate-800 mb-3">Batch Dispatch Timeline</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={scrapHistory.map((h) => ({
                      date: (h.created_at || "").slice(0, 10),
                      qty: Number(h.available_quantity || 0),
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="qty" stroke="#3B82F6" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InventoryDashboard;
